import { useCallback, useState } from 'react';

// NOTE: temporary hardcoded x-ray maintenance windows for 3 sites, tracked for removal under DCP-14880
// NOTE: follow-up (DCP-14880) is to abstract into a data-driven registry (e.g. siteId/date-range/content entries sourced from
// Phil's location API or Contentful) so acknowledgement modals can be toggled per-location without code changes
type XrayMaintenanceWindow = {
	siteId: string;
	// Wall-clock bounds (UTC-encoded) in the location's own timezone, inclusive
	start: number;
	end: number;
	content: string;
};

const XRAY_MAINTENANCE_WINDOWS: XrayMaintenanceWindow[] = [
	{
		siteId: '5239',
		start: Date.UTC(2026, 8, 30, 0, 0, 0),
		end: Date.UTC(2026, 9, 2, 23, 59, 59),
		content:
			'The x-ray unit at this location is out of service from Sept. 30th through Oct. 2nd for scheduled maintenance.',
	},
	{
		siteId: '5424',
		start: Date.UTC(2026, 9, 18, 0, 0, 0),
		end: Date.UTC(2026, 9, 21, 23, 59, 59),
		content:
			'The x-ray unit at this location is out of service from Oct. 18th through Oct. 21st for scheduled maintenance.',
	},
	{
		siteId: '5423',
		start: Date.UTC(2026, 9, 24, 0, 0, 0),
		end: Date.UTC(2026, 9, 29, 23, 59, 59),
		content:
			'The x-ray unit at this location is out of service from Oct. 24th through Oct. 29th for scheduled maintenance.',
	},
];

// Re-expresses "now" as the location's wall-clock date/time, encoded as a UTC instant so it can be
// compared directly against the window bounds above (also wall-clock numbers, UTC-encoded)
const getLocationWallClockNow = (timeZone: string): number => {
	const parts = new Intl.DateTimeFormat('en-CA', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hourCycle: 'h23',
	}).formatToParts(new Date());
	const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
	return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
};

// Finds the maintenance window (if any) covering this site at the location's current wall-clock time
const findActiveMaintenanceWindow = (siteId?: string, ianaTimezone?: string): XrayMaintenanceWindow | undefined => {
	if (!siteId) return undefined;
	// Compare using the location's local wall-clock time, not the visitor's browser timezone
	const locationNow = getLocationWallClockNow(ianaTimezone || 'America/New_York');
	return XRAY_MAINTENANCE_WINDOWS.find(
		(window) => window.siteId === siteId && locationNow >= window.start && locationNow <= window.end
	);
};

// Shared copy so the v2 (XrayMaintenanceAck) and non-versioned (HeroLocation) modals stay in sync
export const XRAY_MAINTENANCE_ACK_TITLE = 'X-Ray imaging currently unavailable at this location';
export const XRAY_MAINTENANCE_ACK_PRIMARY_BUTTON_TEXT = 'Schedule anyway';
export const XRAY_MAINTENANCE_ACK_SECONDARY_BUTTON_TEXT = 'Find a different location';

export type PendingAcknowledgementSelection = { tag: string; key: string };

// Encapsulates whether a location is under x-ray maintenance and the modal/pending-selection
// state needed to gate a tag selection behind user acknowledgement of that maintenance window.
export const useXrayMaintenanceAcknowledgement = (siteId?: string, ianaTimezone?: string) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [pendingSelection, setPendingSelection] = useState<PendingAcknowledgementSelection | null>(null);

	const isUnderMaintenance = useCallback(
		() => Boolean(findActiveMaintenanceWindow(siteId, ianaTimezone)),
		[siteId, ianaTimezone]
	);

	// Returns true when the selection was intercepted (modal opened) so the caller can bail out early.
	const requestAcknowledgement = useCallback(
		(selection: PendingAcknowledgementSelection) => {
			if (!isUnderMaintenance()) return false;
			setPendingSelection(selection);
			setModalOpen(true);
			return true;
		},
		[isUnderMaintenance]
	);

	const closeModal = useCallback(() => {
		setModalOpen(false);
		setPendingSelection(null);
	}, []);

	const activeContent = findActiveMaintenanceWindow(siteId, ianaTimezone)?.content ?? '';

	return { isUnderMaintenance, modalOpen, pendingSelection, requestAcknowledgement, closeModal, activeContent };
};
